'use client';

import { useCallback } from 'react';
import { Keypair, PublicKey } from '@solana/web3.js';
import {
	BaseMessageSignerWalletAdapter,
	WalletReadyState,
} from '@solana/wallet-adapter-base';
import { IWallet } from '@velocity-exchange/sdk';
import useVelocityStore from 'src/stores/VelocityStore/useVelocityStore';
import { useFetchAndSubscribeSubAccounts } from '../user/useFetchAndSubscribeSubaccounts/useFetchAndSubscribeSubaccounts';
import { useSyncDeletedSubaccountsPubkey } from '../user/useFetchAndSubscribeSubaccounts/useSyncDeletedSubaccounts';
import useVelocityClient from 'src/hooks/useVelocityClient';
import useVelocityAccountsStore from 'src/stores/useVelocityAccountsStore';

const useEmulateAccount = () => {
	const velocityClient = useVelocityClient();
	const walletState = useVelocityStore((s) => s.wallet);
	const connection = useVelocityStore((s) => s.connection.current);

	const setVelocityStore = useVelocityStore((s) => s.set);
	const resetAccountsState = useVelocityAccountsStore(
		(s) => s.resetAccountsState
	);

	const { fetchAndSubscribeSubAccounts } = useFetchAndSubscribeSubAccounts();
	const { syncDeletedSubaccountsPubkey } = useSyncDeletedSubaccountsPubkey();

	const emulateAccount = useCallback(
		async (props: { authority: PublicKey; subAccountPubKey?: PublicKey }) => {
			if (!velocityClient || !connection) return;

			setVelocityStore((s) => {
				s.isEmulatingAccount = true;
			});

			const newKeypair = new Keypair({
				publicKey: props.authority.toBytes(),
				secretKey: new Keypair().publicKey.toBytes(),
			});

			const newWallet: BaseMessageSignerWalletAdapter = {
				publicKey: newKeypair.publicKey,
				autoApprove: false,
				connected: true,
				//@ts-ignore
				signTransaction: () => {
					return Promise.resolve();
				},
				//@ts-ignore
				signAllTransactions: () => {
					return Promise.resolve();
				},
				//@ts-ignore
				connect: () => {},
				disconnect: async () => {},
				//@ts-ignore
				on: (_event: string, _fn: () => void) => {},
			};
			if (!newWallet.publicKey) return;

			const success = await velocityClient.updateWallet(
				newWallet as IWallet,
				undefined,
				undefined,
				true
			);

			if (!success) {
				console.log('Error emulating account');
				return;
			}

			const subscribeSuccess = await velocityClient.subscribe();
			if (!subscribeSuccess) {
				velocityClient.unsubscribe();
				console.log('Error subscribing to emulated account');
				return;
			}

			setVelocityStore((state) => {
				state.wallet.current = {
					adapter: newWallet,
					readyState: WalletReadyState.Installed,
				};
			});

			const walletConnectionState = walletState.connectionState;

			walletConnectionState.progress('AdapterConnected');
			walletConnectionState.progress('ClientConnected');

			setVelocityStore((s) => {
				s.wallet.connectionState = walletConnectionState;
			});

			fetchAndSubscribeSubAccounts(props.subAccountPubKey, true);
			syncDeletedSubaccountsPubkey(newWallet.publicKey);

			walletConnectionState.progress('SubaccountsSubscribed');
		},
		[
			velocityClient,
			walletState.connectionState,
			connection,
			setVelocityStore,
			fetchAndSubscribeSubAccounts,
			syncDeletedSubaccountsPubkey,
		]
	);

	const emulateAccountForSigning = useCallback(
		async (props: {
			authority: PublicKey;
			subAccountPubKey?: PublicKey;
		}): Promise<boolean> => {
			if (!velocityClient) return false;

			setVelocityStore((s) => {
				s.isEmulatingAccount = true;
			});

			const success = await velocityClient.emulateAccount(props.authority);

			if (!success) {
				console.log('Error emulating account');
				return false;
			}

			resetAccountsState();

			fetchAndSubscribeSubAccounts(props.subAccountPubKey, true);

			setVelocityStore((s) => {
				s.authorityParam = props.authority.toString();
			});

			return true;
		},
		[
			velocityClient,
			setVelocityStore,
			resetAccountsState,
			fetchAndSubscribeSubAccounts,
		]
	);

	return {
		emulateAccount,
		emulateAccountForSigning,
	};
};

export default useEmulateAccount;
